import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { AppDispatch, RootState } from "..";
import { isAuthEnabled } from "../../config/environment";
import { revokeRefreshToken } from "../../auth/cognito";
import { clearAuthCookies, getRefreshToken } from "../../auth/cookies";
import { resolveSession } from "../../auth/session";
import { leaveProblem } from "../game/gameSlice";

export type AuthStatus = "checking" | "signedIn" | "signedOut";

export interface AuthUser {
  /** Missing when the account has no profile row yet. */
  email?: string;
  name?: string;
}

export interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
}

const initialState: AuthState = {
  status: "checking",
  user: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    signedIn: (state, action: PayloadAction<AuthUser>) => {
      state.status = "signedIn";
      state.user = action.payload;
    },
    signedOut: (state) => {
      state.status = "signedOut";
      state.user = null;
    },
  },
});

export const { signedIn, signedOut } = authSlice.actions;
export default authSlice.reducer;

/** On app load: decide from the cookies whether the visitor is signed in. */
export const initAuth = () => async (dispatch: AppDispatch) => {
  if (!isAuthEnabled()) {
    dispatch(signedOut());
    return;
  }
  const session = await resolveSession();
  if (session.signedIn) {
    dispatch(signedIn({ email: session.user?.email, name: session.user?.name }));
  } else {
    dispatch(signedOut());
  }
};

/**
 * Sign out: clear the cookies, leave any active problem (its attempt stays saved) and revoke
 * the refresh token with Cognito in the background.
 */
export const signOut = () => (dispatch: AppDispatch, getState: () => RootState) => {
  const refreshToken = getRefreshToken();
  clearAuthCookies();
  if (getState().game.activeProblemId) {
    dispatch(leaveProblem());
  }
  dispatch(signedOut());
  if (refreshToken) void revokeRefreshToken(refreshToken);
};
